import { Box, Stack, Tooltip, Typography } from '@mui/material'
import { C1Button } from 'c1ui'
import { C1CircularProgress } from 'c1ui/components/C1CircularProgress/C1CircularProgress'
import { APIError } from 'components/core/errors/api'
import { C1Icon } from 'components/core/icons/C1Icon'
import { InsightItem } from 'components/core/insights'
import { TaskViewLinkV2 } from 'components/core/task-list-v2/task-list-v2-components/TaskViewLinkV2'
import { GrantOriginDrawer } from 'components/pages/entitlement/grant-origin/GrantOriginDrawer'
import { useDrawer } from 'hooks/useDrawer'
import {
    C1AppId,
    GrantReasonDerivedType,
    ManualCatalogResourceTypeId,
    ManualGroupResourceTypeId,
} from 'lib/constants/system'
import { DisplayModeContext } from 'lib/context/app'
import { ClipboardList, ContactRound, Users } from 'lucide-react'
import {
    GrantOriginWithData,
    ListGrantOriginsRequest,
} from 'pbts/c1/api/app/v1/app_entitlement_user_binding'
import { AppResourceServiceGetRequest } from 'pbts/c1/api/app/v1/app_resource'
import { GrantOriginInsight, InsightType } from 'pbts/c1/api/task/v1/insights'
import { TaskServiceGetRequest } from 'pbts/c1/api/task/v1/task'
import { FC, ReactNode, useCallback, useContext } from 'react'
import { FormattedMessage } from 'react-intl'
import { getAppResourceApi } from 'redux_toolkit/app-resources'
import { listGrantOriginsApi } from 'redux_toolkit/entitlements'
import { getTaskApi } from 'redux_toolkit/tasks'

export enum GrantOriginSimplifiedType {
    Catalog = 'Catalog',
    Group = 'Group',
    Ticket = 'Ticket',
    Unknown = 'Unknown',
}

const originTypeToSimplifiedType = (originType: string): GrantOriginSimplifiedType => {
    switch (originType) {
        case GrantReasonDerivedType.GrantReasonCatalogManual:
        case GrantReasonDerivedType.GrantReasonCatalogAutomation:
        case GrantReasonDerivedType.GrantReasonCatalogMembership:
            return GrantOriginSimplifiedType.Catalog
        case GrantReasonDerivedType.GrantReasonGroupManual:
        case GrantReasonDerivedType.GrantReasonGroupAutomation:
            return GrantOriginSimplifiedType.Group
        case GrantReasonDerivedType.GrantReasonRequested:
        default:
            return GrantOriginSimplifiedType.Unknown
    }
}

export const simplifyGrantOrigins = (origins: readonly GrantOriginWithData[]): GrantOriginWithData[] => {
    const simplifiedOrigins: GrantOriginWithData[] = []
    origins.forEach((origin: GrantOriginWithData) => {
        const existingOrigin = simplifiedOrigins.find((simplifiedOrigin: GrantOriginWithData) =>
            simplifiedOrigin.originType === originTypeToSimplifiedType(origin.originType))
        if (existingOrigin) {
            existingOrigin.originData.push(...origin.originData)
        } else {
            simplifiedOrigins.push(GrantOriginWithData.create({
                originType: originTypeToSimplifiedType(origin.originType),
                originData: origin.originData,
            }))
        }
    })
    return simplifiedOrigins
        .filter((origin) => origin.originType !== GrantOriginSimplifiedType.Unknown)
        .sort((a, b) => a.originType.localeCompare(b.originType))
}

const DisplayCatalogGrantOrigin = ({ catalogs }: { catalogs: string[] }): React.ReactElement => {
    return <Tooltip
        title={
            <Stack spacing={1} direction="column">
                <Typography variant="h6">
                    <FormattedMessage id="grant_reason_desc_catalog_header" defaultMessage="Grant origin: Access profile enrollment" />
                </Typography>
                {
                    catalogs.map((name: string) => <Typography key={name}>
                        <FormattedMessage id="grant_reason_desc_catalog" defaultMessage="{name} access profile" values={{ name }} />
                    </Typography>)
                }
            </Stack>
        }
        placement="top"
    >
        <Box component="span"><C1Icon icon={ContactRound} /></Box>
    </Tooltip>
}

const DisplayGroupGrantOrigin = ({ groups }: { groups: string[] }): React.ReactElement => {
    return <Tooltip
        title={
            <Stack spacing={1} direction="column">
                <Typography variant="h6">
                    <FormattedMessage id="grant_reason_desc_group_header" defaultMessage="Grant origin: Inherited from group" />
                </Typography>
                {
                    groups.map((name: string) => <Typography key={name}>
                        <FormattedMessage id="grant_reason_desc_group" defaultMessage="{name} Group" values={{ name }} />
                    </Typography>)
                }
            </Stack>
        }
        placement="top"
    >
        <Box component="span"><C1Icon icon={Users} /></Box>
    </Tooltip>
}

const DisplayTicketGrantOrigin = ({ tickets }: { tickets: string[] }): React.ReactElement => {
    return <Tooltip
        title={
            <Stack spacing={1} direction="column">
                <Typography variant="h6">
                    <FormattedMessage id="grant_reason_desc_ticket_header" defaultMessage="Grant origin: Access request" />
                </Typography>
                {
                    tickets.map((id: string) => <Typography key={id}>
                        <FormattedMessage id="grant_reason_desc_ticket" defaultMessage="Request {id}" values={{ id }} />
                    </Typography>)
                }
            </Stack>
        }
        placement="top"
    >
        <Box component="span"><C1Icon icon={ClipboardList} /></Box>
    </Tooltip>
}

const DisplayGrantOrigin = ({ origin }: { origin: GrantOriginWithData }): React.ReactElement => {
    const dedupedOriginData = Array.from(new Set(origin.originData))
    switch (origin.originType) {
        case GrantOriginSimplifiedType.Catalog:
            return <DisplayCatalogGrantOrigin catalogs={dedupedOriginData} />
        case GrantOriginSimplifiedType.Group:
            return <DisplayGroupGrantOrigin groups={dedupedOriginData} />
        case GrantOriginSimplifiedType.Ticket:
            return <DisplayTicketGrantOrigin tickets={dedupedOriginData} />
        default:
            return <></>
    }
}

const DisplayGrantOrigins = ({
    appId,
    appEntitlementId,
    appUserId,
    grantOrigins,
}: { appId: string, appEntitlementId: string, appUserId: string, grantOrigins: GrantOriginWithData[] }): ReactNode => {
    const displayCtx = useContext(DisplayModeContext)
    const [showDrawer, closeDrawer] = useDrawer()
    const simplifiedGrantOrigins = simplifyGrantOrigins(grantOrigins)

    const openDrawer = useCallback((): void => showDrawer(<GrantOriginDrawer
        appId={appId}
        appEntitlementId={appEntitlementId}
        appUserId={appUserId}
        close={closeDrawer}
    />), [showDrawer, appId, appEntitlementId, appUserId, closeDrawer])

    if (!simplifiedGrantOrigins.length) {
        return null
    }

    const origins = <Stack spacing={1} direction="row">
        {
            simplifiedGrantOrigins.map((origin: GrantOriginWithData, index: number) =>
                <DisplayGrantOrigin key={`${origin.originType}-${index}`} origin={origin} />)
        }
    </Stack>

    if (displayCtx === 'drawer') {
        return <Box>{origins}</Box>
    }

    return <C1Button variant="text" color="info" onClick={openDrawer}>
        {origins}
    </C1Button>
}

export const GrantOrigin = ({
    appId,
    appEntitlementId,
    appUserId,
}: { appId: string, appEntitlementId: string, appUserId: string }): ReactNode => {
    const {
        data,
        isLoading,
        isError,
        error,
    } = listGrantOriginsApi.useListGrantOrigins(
        ListGrantOriginsRequest.create({
            appId,
            appEntitlementId,
            appUserId,
        }),
    )

    if (isError) {
        return <APIError err={error} />
    }

    if (!data?.origins?.length) {
        return isLoading ? <C1CircularProgress /> : null
    }

    return <DisplayGrantOrigins
        appId={appId}
        appEntitlementId={appEntitlementId}
        appUserId={appUserId}
        grantOrigins={data.origins}
    />
}

const DisplayCatalogGrantOriginForInsights = ({ catalogId, insightType }: { catalogId: string, insightType: InsightType }): ReactNode => {
    // From the catalog ID, we need to get the catalog name -- hence the get.
    const {
        data,
        isError,
        error,
    } = getAppResourceApi.useGet(AppResourceServiceGetRequest.create({
        appId: C1AppId,
        appResourceTypeId: ManualCatalogResourceTypeId,
        id: catalogId,
    }))

    if (isError) {
        return <APIError err={error} />
    }

    if (!data) {
        return null
    }

    const name = data.displayName

    return <InsightItem insightType={insightType}>
        <Stack spacing={2} direction="row">
            <Typography>
                <FormattedMessage
                    id="grant_reason_insights_desc_catalog_header_pre"
                    defaultMessage="Grant origin:"
                />
            </Typography>
            <C1Icon icon={ContactRound} />
            <Typography>
                <FormattedMessage
                    id="grant_reason_insights_desc_catalog_header_post"
                    defaultMessage="Enrollment in {name} access profile"
                    values={{ name }}
                />
            </Typography>
        </Stack>
    </InsightItem>
}

const DisplayGroupGrantOriginForInsights = ({ groupId, insightType }: { groupId: string, insightType: InsightType }): ReactNode => {
    // From the group ID, we need to get the group name -- hence the get. We can retrieve the group name from the
    // group resource.
    const {
        data,
        isError,
        error,
    } = getAppResourceApi.useGet(AppResourceServiceGetRequest.create({
        appId: C1AppId,
        appResourceTypeId: ManualGroupResourceTypeId,
        id: groupId,
    }))

    if (isError) {
        return <APIError err={error} />
    }

    if (!data) {
        return null
    }

    const name = data.displayName

    return <InsightItem insightType={insightType}>
        <Stack spacing={2} direction="row">
            <Typography>
                <FormattedMessage
                    id="grant_reason_desc_group_header_pre"
                    defaultMessage="Grant origin:"
                />
            </Typography>
            <C1Icon icon={Users} />
            <Typography>
                <FormattedMessage
                    id="grant_reason_desc_group_header_post"
                    defaultMessage="Inherited from {name} group"
                    values={{ name }}
                />
            </Typography>
        </Stack>
    </InsightItem>
}

const DisplayTicketGrantOriginForInsights = ({ requested, insightType }: { requested: string, insightType: InsightType }): ReactNode => {
    const ticketId = getTicketId(requested)
    if (!ticketId) {
        return null
    }

    const {
        data,
        isError,
        error,
    } = getTaskApi.useGet(
        TaskServiceGetRequest.create({
            id: ticketId,
        }),
        { notify: true },
    )

    if (isError) {
        return <APIError err={error} />
    }

    if (!data) {
        return null
    }

    return <InsightItem insightType={insightType}>
        <Stack spacing={2} direction="row" alignItems="center">
            <Typography>
                <FormattedMessage
                    id="grant_reason_desc_ticket_header_pre"
                    defaultMessage="Grant origin:"
                />
            </Typography>
            <C1Icon icon={ClipboardList} />
            <Typography>
                <FormattedMessage
                    id="grant_reason_desc_ticket_header_post"
                    defaultMessage="Access request"
                />
            </Typography>
            <TaskViewLinkV2 task={data} />
        </Stack>
    </InsightItem>
}

export const getTicketId = (requestedData: string): string | undefined => {
    if (!requestedData) {
        return undefined
    }

    // Format: <id1>:<id2>:<id3> where each ID is 27 alphanumeric characters
    const TICKET_ID_PATTERN = /^([a-zA-Z0-9]{27}):([a-zA-Z0-9]{27}):([a-zA-Z0-9]{27})$/
    const match = requestedData.match(TICKET_ID_PATTERN)

    if (!match) {
        return undefined
    }

    // Return the third ID
    return match[3]
}

export const GrantOriginForInsights: FC<{
    grantOrigin: GrantOriginInsight,
    insightType: InsightType
}> = ({ grantOrigin, insightType }) => {
    if (!grantOrigin.grantReason) {
        return null
    }

    switch (grantOrigin.grantReason.derivedIdType) {
        case GrantReasonDerivedType.GrantReasonCatalogManual:
        case GrantReasonDerivedType.GrantReasonCatalogAutomation:
        case GrantReasonDerivedType.GrantReasonCatalogMembership:
            return <DisplayCatalogGrantOriginForInsights catalogId={grantOrigin.grantReason.derivedIdData} insightType={insightType} />
        case GrantReasonDerivedType.GrantReasonGroupManual:
        case GrantReasonDerivedType.GrantReasonGroupAutomation:
            return <DisplayGroupGrantOriginForInsights groupId={grantOrigin.grantReason.derivedIdData} insightType={insightType} />
        case GrantReasonDerivedType.GrantReasonRequested:
            return <DisplayTicketGrantOriginForInsights requested={grantOrigin.grantReason.derivedIdData} insightType={insightType} />
        default:
            return null
    }
}
